import { Add, Delete, ListAlt } from "@mui/icons-material";
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SysColumn from "app/class/sys-column";
import { BuilderChoiceItem, BuilderFieldDef, TargetColumn } from "../../types/formBuilder.types";
import TranslatableInput from "component/global/translation/TranslatableInput";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { ChoiceOption } from "modules/record/shared/hooks/useChoiceOptions";

interface ChoiceOptionsEditorProps {
    value: BuilderChoiceItem[];
    suggestedKeyBase: string;
    showTranslationKey?: boolean;
    onChange: (value: BuilderChoiceItem[]) => void;
    sourceColumns: SysColumn[];
    labelField: string;
    valueField: string;
    onSourceFieldsChange: (updates: Partial<BuilderFieldDef>) => void;
    mappedOptions?: ChoiceOption[];
}

const sanitizeKeyPart = (value: string, fallback: string) => String(value || fallback)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || fallback;

const emptyItem = (): BuilderChoiceItem => ({ label: "", value: "" });

const ChoiceOptionsEditor = ({ value, suggestedKeyBase, showTranslationKey = false, onChange, sourceColumns, labelField, valueField, onSourceFieldsChange, mappedOptions }: ChoiceOptionsEditorProps) => {
    const { t } = useTranslation();
    const columnLabel = (column: SysColumn | TargetColumn) => `${translateMetadataLabel(t, column) || column.label || column.name} (${column.name})`;
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<BuilderChoiceItem[]>(value.length ? value : [emptyItem()]);
    const draftRef = useRef(draft);
    const nextRowKey = useRef(1);
    const rowKeys = useRef(draft.map(() => nextRowKey.current++));
    const flushers = useRef(new Map<number, () => Promise<boolean>>());
    const pendingLabels = useRef(new Map<number, string>());
    const [duplicateValues, setDuplicateValues] = useState<Set<number>>(new Set());
    const [applyError, setApplyError] = useState("");
    const selectedLabelColumn = sourceColumns.find((column) => column.name === labelField) || null;
    const selectedValueColumn = sourceColumns.find((column) => column.name === valueField) || null;

    useEffect(() => {
        if (!open) return;
        const next = value.length ? value.map((item) => ({ ...item })) : [emptyItem()];
        draftRef.current = next;
        rowKeys.current = next.map(() => nextRowKey.current++);
        pendingLabels.current.clear();
        setDraft(next);
        setDuplicateValues(new Set());
        setApplyError("");
    }, [open, value]);

    const updateItem = (index: number, updates: Partial<BuilderChoiceItem>) => {
        const next = draftRef.current.map((item, itemIndex) => itemIndex === index ? { ...item, ...updates } : item);
        draftRef.current = next;
        setDraft(next);
        setDuplicateValues(new Set());
        setApplyError("");
    };

    const removeItem = (index: number) => {
        const next = draftRef.current.filter((_, itemIndex) => itemIndex !== index);
        draftRef.current = next.length ? next : [emptyItem()];
        rowKeys.current.splice(index, 1);
        if (!next.length) rowKeys.current = [nextRowKey.current++];
        flushers.current.clear();
        pendingLabels.current = new Map(Array.from(pendingLabels.current.entries())
            .filter(([itemIndex]) => itemIndex !== index)
            .map(([itemIndex, label]) => [itemIndex > index ? itemIndex - 1 : itemIndex, label]));
        setDraft(draftRef.current);
        setDuplicateValues(new Set());
    };

    const moveItem = (index: number, delta: number) => {
        const next = [...draftRef.current];
        [next[index], next[index + delta]] = [next[index + delta], next[index]];
        [rowKeys.current[index], rowKeys.current[index + delta]] = [rowKeys.current[index + delta], rowKeys.current[index]];
        const firstPending = pendingLabels.current.get(index);
        const secondPending = pendingLabels.current.get(index + delta);
        if (secondPending === undefined) pendingLabels.current.delete(index); else pendingLabels.current.set(index, secondPending);
        if (firstPending === undefined) pendingLabels.current.delete(index + delta); else pendingLabels.current.set(index + delta, firstPending);
        draftRef.current = next;
        flushers.current.clear();
        setDraft(next);
        setDuplicateValues(new Set());
    };

    const save = async () => {
        const candidates = draftRef.current
            .map((item, index) => ({ ...item, value: item.value.trim() || sanitizeKeyPart(pendingLabels.current.get(index) || item.label, "option"), hasContent: Boolean(item.label.trim() || item.value.trim() || pendingLabels.current.get(index)?.trim()), index }))
            .filter((item) => item.hasContent);
        const seen = new Map<string, number>();
        const duplicates = new Set<number>();
        candidates.forEach((item) => {
            if (seen.has(item.value)) {
                duplicates.add(seen.get(item.value)!);
                duplicates.add(item.index);
            } else seen.set(item.value, item.index);
        });
        if (duplicates.size) {
            setDuplicateValues(duplicates);
            setApplyError(String(t("Option values must be unique.")));
            return;
        }
        for (const flush of flushers.current.values()) {
            if (!(await flush())) return;
        }
        const options = draftRef.current
            .map((item, index) => ({ ...item, value: item.value.trim() || sanitizeKeyPart(pendingLabels.current.get(index) || item.label, "option") }));
        onChange(options.filter((item, index) => Boolean(draftRef.current[index].label.trim() || draftRef.current[index].value.trim() || pendingLabels.current.get(index)?.trim())));
        setOpen(false);
    };

    return (
        <Box>
            {mappedOptions && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    {t("service_builder.choice.mapped_options", { defaultValue: "Options come from the mapped column. Static options below override them when set." })}
                    <Box>{mappedOptions.map((option) => `${option.label} (${option.value})`).join(", ")}</Box>
                </Alert>
            )}
            <Button size="small" variant="outlined" startIcon={<ListAlt />} onClick={() => setOpen(true)} fullWidth>
                {t("Edit dropdown options")} ({value.length})
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{t("Dropdown options")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        {sourceColumns.length > 0 && (
                            <>
                                <Alert severity="info">
                                    {t("A source table is configured for this field (in the sidebar). While set, options below load dynamically from that table using the label/value fields chosen here — the static list below is only used as a fallback if the source table is removed.")}
                                </Alert>
                                <Stack direction="row" spacing={1.5}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        options={sourceColumns}
                                        value={selectedLabelColumn}
                                        onChange={(_, column) => onSourceFieldsChange({ display: column?.name || "" })}
                                        getOptionLabel={columnLabel}
                                        isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                        renderInput={(params) => <TextField {...params} label={t("Label field")} helperText={t("Column shown to users as each option's label.")} />}
                                    />
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        options={sourceColumns}
                                        value={selectedValueColumn}
                                        onChange={(_, column) => onSourceFieldsChange({ valueField: column?.name || "" })}
                                        getOptionLabel={columnLabel}
                                        isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                        renderInput={(params) => <TextField {...params} label={t("Value field")} helperText={t("Column stored as each option's value (defaults to id).")} />}
                                    />
                                </Stack>
                            </>
                        )}
                        {draft.map((item, index) => {
                            const keyPart = sanitizeKeyPart(item.value || item.label, `option_${index + 1}`);
                            return (
                                <Box key={rowKeys.current[index]} className="service-builder__choice-option-row">
                                    <TranslatableInput
                                        label="Label"
                                        value={item.label}
                                        suggestedKey={`${suggestedKeyBase}.${keyPart}.label`}
                                        defaultPublic={true}
                                        showTranslationKey={showTranslationKey}
                                        onChange={(label) => updateItem(index, { label })}
                                        onDraftChange={(label) => { pendingLabels.current.set(index, label); }}
                                        registerFlush={(flush) => { flushers.current.set(index, flush); return () => { if (flushers.current.get(index) === flush) flushers.current.delete(index); }; }}
                                    />
                                    <TextField
                                        size="small"
                                        label={t("Value")}
                                        value={item.value}
                                        onChange={(event) => updateItem(index, { value: event.target.value })}
                                        error={duplicateValues.has(index)}
                                        helperText={duplicateValues.has(index) ? t("Option values must be unique.") : undefined}
                                        fullWidth
                                    />
                                    <IconButton size="small" aria-label={String(t("Move option {{option}} up", { option: item.label || item.value || index + 1 }))} disabled={index === 0} onClick={() => moveItem(index, -1)}>↑</IconButton>
                                    <IconButton size="small" aria-label={String(t("Move option {{option}} down", { option: item.label || item.value || index + 1 }))} disabled={index === draft.length - 1} onClick={() => moveItem(index, 1)}>↓</IconButton>
                                    <Tooltip title={t("Remove option")}>
                                        <span>
                                            <IconButton size="small" color="error" aria-label={`${t("Remove option")} ${item.label || item.value || index + 1}`} onClick={() => removeItem(index)} disabled={draft.length === 1 && !item.label && !item.value}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => { draftRef.current = [...draftRef.current, emptyItem()]; rowKeys.current.push(nextRowKey.current++); setDraft(draftRef.current); }}>
                            {t("Add option")}
                        </Button>
                    </Stack>
                </DialogContent>
                {applyError && <Alert severity="error" sx={{ mx: 3 }}>{applyError}</Alert>}
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                    <Button variant="contained" onClick={save}>{t("Apply")}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChoiceOptionsEditor;
